import React, { useCallback, useEffect, useMemo, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"
import StatusBanner from "../../../shared/components/StatusBanner"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusPill from "../../../shared/components/StatusPill"

const API_BASE = (() => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1"

    return isLocalHost ? (["http://localhost", "3000/api"].join(":")) : "/api"
  }

  return "/api"
})()

function isMobileGrid() {
  if (typeof window === "undefined") return false
  return window.innerWidth < 980
}

function buildSuggestedTreatment(condition) {
  const key = String(condition || "").toUpperCase()

  if (key.includes("CARIES")) return "Resina / RestauraciÃ³n"
  if (key.includes("RESTOR")) return "RevisiÃ³n de restauraciÃ³n"
  if (key.includes("ENDO")) return "ValoraciÃ³n endodÃ³ntica"
  if (key.includes("FRACT")) return "ReconstrucciÃ³n / Corona"
  if (key.includes("EXTRACT")) return "ExtracciÃ³n / ReposiciÃ³n"
  if (key.includes("IMPLANT")) return "ValoraciÃ³n protÃ©sica"
  if (key.includes("HEALTHY")) return "Control / Seguimiento"
  return "ValoraciÃ³n clínica"
}

function buildSuggestedPrice(condition) {
  const key = String(condition || "").toUpperCase()

  if (key.includes("CARIES")) return 800
  if (key.includes("RESTOR")) return 600
  if (key.includes("ENDO")) return 3500
  if (key.includes("FRACT")) return 2500
  if (key.includes("EXTRACT")) return 1500
  if (key.includes("IMPLANT")) return 12000
  if (key.includes("HEALTHY")) return 0
  return 0
}

function getPlanBalance(plan) {
  return Math.max(0, Number(plan?.totalEstimated || 0) - Number(plan?.totalPaid || 0))
}

function getPlanTone(plan) {
  const status = String(plan?.status || "").toUpperCase()
  const balance = getPlanBalance(plan)

  if (status === "COMPLETED") {
    return {
      border: "#bbf7d0",
      background: "linear-gradient(180deg, #ffffff 0%, #f8fff9 100%)",
      accent: "#16a34a",
    }
  }

  if (balance > 0) {
    return {
      border: "#fde68a",
      background: "linear-gradient(180deg, #ffffff 0%, #fffdf7 100%)",
      accent: "#d97706",
    }
  }

  return {
    border: "#dbeafe",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    accent: "#2563eb",
  }
}

export default function PlansTab({
  patientPlans,
  patientAppointments,
  selectedPatientId,
  clinicId,
  patientName,
  formatMoney,
  navigate,
  isReadOnly,
  readOnlyReason,
  onReloadPlans,
}) {
  const compact = isMobileGrid()

  const [odontogramPrefill, setOdontogramPrefill] = useState(null)
  const [creatingPlan, setCreatingPlan] = useState(false)
  const [planForm, setPlanForm] = useState({
    title: "",
    suggestedTreatment: "",
    estimatedPrice: "",
    priority: "MEDIA",
    notes: "",
  })

  const [resolvingTreatment, setResolvingTreatment] = useState(false)
  const [resolvedTreatment, setResolvedTreatment] = useState(null)
  const [treatmentCandidates, setTreatmentCandidates] = useState([])
  const [catalogTreatments, setCatalogTreatments] = useState([])
  const [selectedTreatmentId, setSelectedTreatmentId] = useState("")
  const [resolverMessage, setResolverMessage] = useState("")
  const [showQuickTreatmentForm, setShowQuickTreatmentForm] = useState(false)
  const [savingQuickTreatment, setSavingQuickTreatment] = useState(false)

  const [quickTreatmentForm, setQuickTreatmentForm] = useState({
    name: "",
    price: "",
    durationMinutes: "30",
    requiredSpecialty: "",
    recallDays: "",
    odontogramCondition: "",
  })

  const resetResolverState = useCallback(() => {
    setResolvedTreatment(null)
    setTreatmentCandidates([])
    setSelectedTreatmentId("")
    setResolverMessage("")
    setShowQuickTreatmentForm(false)
  }, [])

  const loadCatalogTreatments = useCallback(async () => {
    try {
      if (!clinicId) {
        setCatalogTreatments([])
        return
      }

      const params = new URLSearchParams({ clinicId })
      const res = await apiFetch(`${API_BASE}/treatments?${params.toString()}`)
      const data = await res.json().catch(() => [])

      setCatalogTreatments(res.ok && Array.isArray(data) ? data : [])
    } catch {
      setCatalogTreatments([])
    }
  }, [clinicId])

  const resolveIncomingTreatment = useCallback(
    async (prefill) => {
      try {
        if (!prefill || !clinicId) {
          resetResolverState()
          return
        }

        setResolvingTreatment(true)
        resetResolverState()

        const isDiagnosisSource =
          String(prefill?.source || "").trim().toUpperCase() === "INITIAL_DIAGNOSIS"

        const endpoint = isDiagnosisSource
          ? `${API_BASE}/appointments/resolve-treatment-from-diagnosis`
          : `${API_BASE}/appointments/resolve-treatment-from-odontogram`

        const payload = isDiagnosisSource
          ? {
              clinicId,
              mainDiagnosis: prefill?.mainDiagnosis || "",
              secondaryDiagnosis: prefill?.secondaryDiagnosis || "",
              suggestedTreatment: prefill?.suggestedTreatment || "",
              recommendedTreatment: prefill?.recommendedTreatment || "",
            }
          : {
              clinicId,
              condition: prefill?.condition || "",
              definitionType: prefill?.definitionType || "TOOTH",
            }

        const res = await apiFetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          setResolverMessage(data?.message || "No se pudo resolver tratamiento.")
          return
        }

        setResolverMessage(data?.reason || "")
        setTreatmentCandidates(Array.isArray(data?.candidates) ? data.candidates : [])

        if (data?.resolved && data?.selectedTreatment?.id) {
          setResolvedTreatment(data.selectedTreatment)
          setSelectedTreatmentId(String(data.selectedTreatment.id))

          setPlanForm((prev) => ({
            ...prev,
            suggestedTreatment: data.selectedTreatment.name || prev.suggestedTreatment,
            estimatedPrice:
              prev.estimatedPrice ||
              (data.selectedTreatment.price != null ? String(data.selectedTreatment.price) : ""),
          }))
        }
      } catch {
        setResolverMessage("Error al resolver tratamiento.")
      } finally {
        setResolvingTreatment(false)
      }
    },
    [clinicId, resetResolverState],
  )

  useEffect(() => {
    loadCatalogTreatments()
  }, [loadCatalogTreatments])

  useEffect(() => {
    const rawDiagnosis = localStorage.getItem("odontoflow_plan_prefill_diagnosis")
    const rawOdontogram = localStorage.getItem("odontoflow_plan_prefill")
    const raw = rawDiagnosis || rawOdontogram

    if (!raw) {
      setOdontogramPrefill(null)
      resetResolverState()
      setPlanForm({
        title: "",
        suggestedTreatment: "",
        estimatedPrice: "",
        priority: "MEDIA",
        notes: "",
      })
      return
    }

    try {
      const parsed = JSON.parse(raw)

      if (
        String(parsed?.patientId || "") === String(selectedPatientId || "") &&
        String(parsed?.clinicId || "") === String(clinicId || "")
      ) {
        setOdontogramPrefill(parsed)

        const isDiagnosisSource =
          String(parsed?.source || "").trim().toUpperCase() === "INITIAL_DIAGNOSIS"

        if (isDiagnosisSource) {
          const diagnosisTooth = parsed?.tooth || ""
          const diagnosisSurface =
            parsed?.surface && String(parsed.surface).trim().toUpperCase() !== "ALL"
              ? parsed.surface
              : "No aplica"

          setPlanForm({
            title:
              parsed?.title ||
              `Plan derivado de diagnÃ³stico${diagnosisTooth ? ` Â· Pieza ${diagnosisTooth}` : ""}`,
            suggestedTreatment:
              parsed?.suggestedTreatment ||
              parsed?.recommendedTreatment ||
              parsed?.mainDiagnosis ||
              "",
            estimatedPrice: parsed?.estimatedPrice ? String(parsed.estimatedPrice) : "",
            priority: parsed?.priority || "MEDIA",
            notes:
              parsed?.notes ||
              `DiagnÃ³stico inicial enviado desde historia clínica.\n` +
                `DiagnÃ³stico principal: ${parsed?.mainDiagnosis || "-"}\n` +
                `DiagnÃ³stico secundario: ${parsed?.secondaryDiagnosis || "-"}\n` +
                `Pieza: ${diagnosisTooth || "-"}\n` +
                `Superficie: ${diagnosisSurface}\n` +
                `Severidad: ${parsed?.severity || "-"}`,
          })
        } else {
          const suggestedTreatment = buildSuggestedTreatment(parsed?.condition)
          const estimatedPrice = buildSuggestedPrice(parsed?.condition)

          const definitionType =
            String(parsed?.definitionType || "").trim().toUpperCase() === "SURFACE"
              ? "SURFACE"
              : "TOOTH"

          const surfaceLabel = definitionType === "SURFACE" ? parsed?.surface || "O" : "Diente completo"

          setPlanForm({
            title:
              definitionType === "SURFACE"
                ? `Plan pieza ${parsed?.tooth || ""} Â· ${surfaceLabel}`
                : `Plan general pieza ${parsed?.tooth || ""}`,
            suggestedTreatment,
            estimatedPrice: estimatedPrice ? String(estimatedPrice) : "",
            priority: "MEDIA",
            notes:
              `Hallazgo enviado desde odontograma.\n` +
              `Pieza: ${parsed?.tooth || "-"}\n` +
              `Tipo: ${definitionType === "SURFACE" ? "Superficie" : "Diente completo"}\n` +
              `Superficie: ${definitionType === "SURFACE" ? surfaceLabel : "No aplica"}\n` +
              `CondiciÃ³n: ${parsed?.condition || "-"}`,
          })
        }

        const initialCondition =
          !isDiagnosisSource && parsed?.condition
            ? String(parsed.condition).trim().toUpperCase()
            : ""

        setQuickTreatmentForm((prev) => ({
          ...prev,
          odontogramCondition: initialCondition || prev.odontogramCondition || "",
        }))

        resolveIncomingTreatment(parsed)
      } else {
        setOdontogramPrefill(null)
        resetResolverState()
      }
    } catch {
      setOdontogramPrefill(null)
      resetResolverState()
    }
  }, [selectedPatientId, clinicId, resetResolverState, resolveIncomingTreatment])

  const clearPrefill = () => {
    localStorage.removeItem("odontoflow_plan_prefill")
    localStorage.removeItem("odontoflow_plan_prefill_diagnosis")

    setOdontogramPrefill(null)
    resetResolverState()

    setQuickTreatmentForm({
      name: "",
      price: "",
      durationMinutes: "30",
      requiredSpecialty: "",
      recallDays: "",
      odontogramCondition: "",
    })

    setPlanForm({
      title: "",
      suggestedTreatment: "",
      estimatedPrice: "",
      priority: "MEDIA",
      notes: "",
    })
  }

  const isDiagnosisPrefill =
    String(odontogramPrefill?.source || "").trim().toUpperCase() === "INITIAL_DIAGNOSIS"

  const selectableTreatments = useMemo(() => {
    const map = new Map()

    ;[...catalogTreatments, ...treatmentCandidates].forEach((row) => {
      if (!row?.id) return
      map.set(String(row.id), row)
    })

    return Array.from(map.values()).sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base",
      }),
    )
  }, [catalogTreatments, treatmentCandidates])

  const handleSelectTreatment = (value) => {
    setSelectedTreatmentId(value)

    const picked = selectableTreatments.find((item) => String(item.id) === String(value)) || null
    setResolvedTreatment(picked)

    if (picked) {
      setPlanForm((prev) => ({
        ...prev,
        suggestedTreatment: picked.name || prev.suggestedTreatment,
        estimatedPrice: picked.price != null ? String(picked.price) : prev.estimatedPrice,
      }))
    }
  }

  const handleQuickCreateTreatment = async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!clinicId) {
        alert("No hay una clínica válida seleccionada.")
        return
      }

      if (!quickTreatmentForm.name.trim()) {
        alert("Escribe el nombre del tratamiento.")
        return
      }

      setSavingQuickTreatment(true)

      const res = await apiFetch(`${API_BASE}/treatments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clinicId,
          name: quickTreatmentForm.name.trim(),
          durationMinutes: Number(quickTreatmentForm.durationMinutes || 30),
          price: Number(quickTreatmentForm.price || 0),
          requiredSpecialty: quickTreatmentForm.requiredSpecialty || null,
          recallDays: quickTreatmentForm.recallDays ? Number(quickTreatmentForm.recallDays) : null,
          odontogramCondition: quickTreatmentForm.odontogramCondition || null,
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el tratamiento.")
        return
      }

      if (!isDiagnosisPrefill) {
        const mappingCondition =
          quickTreatmentForm.odontogramCondition ||
          String(odontogramPrefill?.condition || "").trim().toUpperCase()

        if (mappingCondition && data?.id) {
          await apiFetch(`${API_BASE}/treatments/assign-odontogram-condition`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              clinicId,
              treatmentId: data.id,
              condition: mappingCondition,
              definitionType:
                String(odontogramPrefill?.definitionType || "").trim().toUpperCase() === "SURFACE"
                  ? "SURFACE"
                  : "TOOTH",
            }),
          }).catch(() => null)
        }
      }

      await loadCatalogTreatments()

      const createdTreatment = {
        id: data?.id,
        name: data?.name || quickTreatmentForm.name.trim(),
        price: data?.price != null ? Number(data.price) : Number(quickTreatmentForm.price || 0),
        durationMinutes:
          data?.durationMinutes != null
            ? Number(data.durationMinutes)
            : Number(quickTreatmentForm.durationMinutes || 30),
        requiredSpecialty: data?.requiredSpecialty || quickTreatmentForm.requiredSpecialty || null,
      }

      setResolvedTreatment(createdTreatment)
      setSelectedTreatmentId(String(createdTreatment.id))

      setPlanForm((prev) => ({
        ...prev,
        suggestedTreatment: createdTreatment.name || prev.suggestedTreatment,
        estimatedPrice: String(createdTreatment.price || 0),
      }))

      setTreatmentCandidates((prev) => {
        const next = [...prev]
        const exists = next.some((item) => String(item.id) === String(createdTreatment.id))
        if (!exists) next.unshift(createdTreatment)
        return next
      })

      setQuickTreatmentForm({
        name: "",
        price: "",
        durationMinutes: "30",
        requiredSpecialty: "",
        recallDays: "",
        odontogramCondition: !isDiagnosisPrefill
          ? String(odontogramPrefill?.condition || "").trim().toUpperCase()
          : "",
      })

      setShowQuickTreatmentForm(false)
      setResolverMessage("Tratamiento creado y seleccionado correctamente.")
      alert("Tratamiento creado correctamente.")
    } catch {
      alert("Error al crear el tratamiento.")
    } finally {
      setSavingQuickTreatment(false)
    }
  }

  const createRealPlan = async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!odontogramPrefill) {
        alert("No hay sugerencia disponible.")
        return
      }

      if (!selectedPatientId || !clinicId) {
        alert("Faltan datos de paciente o clínica.")
        return
      }

      if (!planForm.title.trim()) {
        alert("Escribe un tÃ­tulo para el plan.")
        return
      }

      if (!selectedTreatmentId) {
        alert("Primero resuelve el tratamiento real del catÃ¡logo.")
        return
      }

      setCreatingPlan(true)

      if (isDiagnosisPrefill) {
        const res = await apiFetch(`${API_BASE}/appointments/create-plan-from-diagnosis`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            clinicId,
            patientId: selectedPatientId,
            formInstanceId: odontogramPrefill.formInstanceId || undefined,
            treatmentId: selectedTreatmentId || undefined,
            title: planForm.title.trim(),
            mainDiagnosis: odontogramPrefill.mainDiagnosis || "",
            secondaryDiagnosis: odontogramPrefill.secondaryDiagnosis || "",
            diagnosisType: odontogramPrefill.diagnosisType || "PRELIMINARY",
            severity: odontogramPrefill.severity || "MEDIUM",
            priority: planForm.priority || odontogramPrefill.priority || "MEDIA",
            tooth: odontogramPrefill.tooth || undefined,
            surface: odontogramPrefill.surface || undefined,
            suggestedTreatment: planForm.suggestedTreatment || odontogramPrefill.suggestedTreatment || "",
            recommendedTreatment: odontogramPrefill.recommendedTreatment || "",
            estimatedPrice: planForm.estimatedPrice ? Number(planForm.estimatedPrice) : undefined,
            notes: planForm.notes.trim(),
          }),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo crear el plan desde el diagnÃ³stico inicial.")
          return
        }

        clearPrefill()
        await onReloadPlans?.()
        alert("Plan creado correctamente desde diagnÃ³stico inicial.")
        return
      }

      const definitionType =
        String(odontogramPrefill?.definitionType || "").trim().toUpperCase() === "SURFACE"
          ? "SURFACE"
          : "TOOTH"

      const res = await apiFetch(`${API_BASE}/appointments/create-plan-from-odontogram`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clinicId,
          patientId: selectedPatientId,
          treatmentId: selectedTreatmentId || undefined,
          tooth: odontogramPrefill.tooth,
          surface: definitionType === "SURFACE" ? odontogramPrefill.surface || "O" : null,
          definitionType,
          condition: odontogramPrefill.condition,
          title: planForm.title.trim(),
          notes: planForm.notes.trim(),
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el plan real.")
        return
      }

      clearPrefill()
      await onReloadPlans?.()
      alert("Plan creado correctamente.")
    } catch {
      alert("Error al crear el plan.")
    } finally {
      setCreatingPlan(false)
    }
  }

  const schedulePlanItem = (plan, item) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura")
      return
    }

    if (!selectedPatientId) {
      alert("No hay paciente seleccionado.")
      return
    }

    if (!clinicId) {
      alert("No hay una clínica válida seleccionada.")
      return
    }

    if (!item?.treatmentId) {
      alert("Este Ã­tem no tiene tratamiento ligado.")
      return
    }

    if (item.appointmentId) {
      alert("Este tratamiento ya tiene una cita programada.")
      return
    }

    localStorage.setItem(
      "odontoflow_new_appointment_context",
      JSON.stringify({
        patientId: selectedPatientId,
        clinicId,
        treatmentId: item.treatmentId,
        treatmentPlanItemId: item.id,
        treatmentPlanId: plan.id,
        tooth: item.tooth ?? null,
        surface: item.surface || "ALL",
        source: "patients_plan_tab",
      }),
    )

    navigate("/agenda")
  }

  const getLinkedAppointmentDate = (item) => {
    const directDate =
      item?.appointment?.startTime || item?.appointment?.start || item?.appointment?.date || null

    if (directDate) return directDate

    if (!item?.appointmentId) return null

    const linked = (patientAppointments || []).find(
      (appt) => String(appt.id) === String(item.appointmentId),
    )

    return linked?.start || linked?.startTime || null
  }

  const openManualPlanPage = () => {
    if (!selectedPatientId || !clinicId) {
      alert("Faltan datos de paciente o clínica.")
      return
    }

    const params = new URLSearchParams()
    params.set("clinicId", String(clinicId))
    params.set("patientId", String(selectedPatientId))

    if (String(patientName || "").trim()) {
      params.set("patientName", String(patientName).trim())
    }

    navigate(`/treatment-plans?${params.toString()}`)
  }

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {isReadOnly ? (
        <StatusBanner tone="danger" title="Planes en solo lectura" text={readOnlyReason} />
      ) : null}

      {odontogramPrefill ? (
        <SectionCard
          title={
            isDiagnosisPrefill
              ? "Sugerencia enviada desde diagnÃ³stico inicial"
              : "Sugerencia enviada desde odontograma"
          }
          subtitle={
            isDiagnosisPrefill
              ? "Valida el tratamiento real del catÃ¡logo antes de crear el plan."
              : "Convierte el hallazgo clÃ­nico en un plan real, ya ligado al catÃ¡logo."
          }
          actions={
            <button
              style={{
                ...ui.secondaryButton,
                background: "#ffffff",
              }}
              onClick={clearPrefill}
            >
              Limpiar sugerencia
            </button>
          }
        >
          <div
            style={{
              display: "grid",
              gap: 14,
              border: "1px solid #dbeafe",
              background: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
              borderRadius: 18,
              padding: 16,
              boxShadow: "0 8px 22px rgba(15,23,42,0.05)",
            }}
          >
            <div style={{ display: "grid", gap: 8 }}>
              <div
                style={{
                  display: "inline-flex",
                  width: "fit-content",
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  fontSize: 10.5,
                  fontWeight: 900,
                  letterSpacing: 0.45,
                  textTransform: "uppercase",
                }}
              >
                Continuidad clínica
              </div>

              <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 18, letterSpacing: "-0.02em" }}>
                {isDiagnosisPrefill
                  ? "DiagnÃ³stico listo para continuar en planes"
                  : "Hallazgo listo para convertirse en plan"}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <StatusPill tone="info">Pieza {odontogramPrefill?.tooth || "-"}</StatusPill>

                {isDiagnosisPrefill ? (
                  <StatusPill tone="neutral">
                    Superficie{" "}
                    {(odontogramPrefill.surface &&
                      String(odontogramPrefill.surface).trim().toUpperCase() !== "ALL")
                      ? odontogramPrefill.surface
                      : "No aplica"}
                  </StatusPill>
                ) : (
                  <StatusPill tone="neutral">
                    {String(odontogramPrefill?.definitionType || "").trim().toUpperCase() === "SURFACE"
                      ? `Superficie ${odontogramPrefill.surface || "O"}`
                      : "Diente completo"}
                  </StatusPill>
                )}

                <StatusPill tone="neutral">
                  {isDiagnosisPrefill
                    ? `DiagnÃ³stico ${odontogramPrefill.mainDiagnosis || "-"}`
                    : `CondiciÃ³n ${odontogramPrefill.condition || "-"}`}
                </StatusPill>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: compact ? "1fr" : "1.2fr 1fr 0.8fr",
                gap: 12,
              }}
            >
              <FieldBlock label="TÃ­tulo del plan">
                <input
                  value={planForm.title}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  style={ui.input}
                  disabled={isReadOnly}
                />
              </FieldBlock>

              <FieldBlock label="Tratamiento sugerido">
                <input
                  value={planForm.suggestedTreatment}
                  readOnly
                  style={{ ...ui.input, background: "#f8fafc" }}
                />
              </FieldBlock>

              <FieldBlock label="Precio base sugerido">
                <input
                  type="number"
                  value={planForm.estimatedPrice}
                  readOnly
                  style={{ ...ui.input, background: "#f8fafc" }}
                />
              </FieldBlock>
            </div>

            <FieldBlock label="Notas clínicas">
              <textarea
                rows={4}
                value={planForm.notes}
                onChange={(e) =>
                  setPlanForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                style={ui.textarea}
                disabled={isReadOnly}
              />
            </FieldBlock>

            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 16,
                background: "#ffffff",
                padding: 14,
                display: "grid",
                gap: 12,
                boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <div
                  style={{
                    fontWeight: 900,
                    color: "#0f172a",
                    fontSize: 16,
                    letterSpacing: "-0.02em",
                  }}
                >
                  ResoluciÃ³n de tratamiento real
                </div>

                <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.4 }}>
                  AquÃ­ validas el tratamiento correcto del catÃ¡logo antes de crear el plan.
                </div>
              </div>

              {resolvingTreatment ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>Resolviendo tratamiento...</div>
              ) : null}

              {resolverMessage ? (
                <div
                  style={{
                    fontSize: 13,
                    color: "#475569",
                    lineHeight: 1.4,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {resolverMessage}
                </div>
              ) : null}

              {resolvedTreatment ? (
                <div
                  style={{
                    border: "1px solid #bbf7d0",
                    background: "#f0fdf4",
                    borderRadius: 14,
                    padding: 12,
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#166534" }}>Tratamiento resuelto</div>
                  <div style={{ fontSize: 14, color: "#166534", fontWeight: 800 }}>
                    {resolvedTreatment.name}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#166534" }}>
                    Precio: {formatMoney(resolvedTreatment.price || 0)}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    border: "1px solid #fed7aa",
                    background: "#fff7ed",
                    borderRadius: 14,
                    padding: 12,
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#c2410c" }}>
                    No se resolviÃ³ automÃ¡ticamente
                  </div>

                  <FieldBlock label="Selecciona tratamiento existente">
                    <select
                      value={selectedTreatmentId}
                      onChange={(e) => handleSelectTreatment(e.target.value)}
                      style={ui.input}
                      disabled={isReadOnly}
                    >
                      <option value="">Selecciona</option>
                      {selectableTreatments.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} Â· {formatMoney(item.price || 0)}
                        </option>
                      ))}
                    </select>
                  </FieldBlock>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={ui.secondaryButton}
                      onClick={() => setShowQuickTreatmentForm((prev) => !prev)}
                      disabled={isReadOnly}
                    >
                      {showQuickTreatmentForm ? "Cerrar alta rÃ¡pida" : "Crear tratamiento rÃ¡pido"}
                    </button>

                    <button
                      type="button"
                      style={{
                        ...ui.secondaryButton,
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        color: "#1d4ed8",
                      }}
                      onClick={() => resolveIncomingTreatment(odontogramPrefill)}
                    >
                      Reintentar resoluciÃ³n
                    </button>
                  </div>

                  {showQuickTreatmentForm ? (
                    <div
                      style={{
                        border: "1px solid #fdba74",
                        background: "#ffffff",
                        borderRadius: 14,
                        padding: 12,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div style={{ fontWeight: 900, color: "#9a3412" }}>
                        Alta rÃ¡pida de tratamiento
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: compact ? "1fr" : "1.2fr 0.8fr 0.8fr",
                          gap: 12,
                        }}
                      >
                        <FieldBlock label="Nombre">
                          <input
                            value={quickTreatmentForm.name}
                            onChange={(e) =>
                              setQuickTreatmentForm((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            style={ui.input}
                            disabled={isReadOnly}
                          />
                        </FieldBlock>

                        <FieldBlock label="Precio">
                          <input
                            type="number"
                            value={quickTreatmentForm.price}
                            onChange={(e) =>
                              setQuickTreatmentForm((prev) => ({
                                ...prev,
                                price: e.target.value,
                              }))
                            }
                            style={ui.input}
                            disabled={isReadOnly}
                          />
                        </FieldBlock>

                        <FieldBlock label="Duración (min)">
                          <input
                            type="number"
                            value={quickTreatmentForm.durationMinutes}
                            onChange={(e) =>
                              setQuickTreatmentForm((prev) => ({
                                ...prev,
                                durationMinutes: e.target.value,
                              }))
                            }
                            style={ui.input}
                            disabled={isReadOnly}
                          />
                        </FieldBlock>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: compact ? "1fr" : "repeat(3, minmax(0,1fr))",
                          gap: 12,
                        }}
                      >
                        <FieldBlock label="Especialidad requerida">
                          <select
                            value={quickTreatmentForm.requiredSpecialty}
                            onChange={(e) =>
                              setQuickTreatmentForm((prev) => ({
                                ...prev,
                                requiredSpecialty: e.target.value,
                              }))
                            }
                            style={ui.input}
                            disabled={isReadOnly}
                          >
                            <option value="">Sin restricciÃ³n</option>
                            <option value="GENERAL">GENERAL</option>
                            <option value="ORTODONCIA">ORTODONCIA</option>
                            <option value="ENDODONCIA">ENDODONCIA</option>
                            <option value="CIRUGIA">CIRUGIA</option>
                            <option value="IMPLANTOLOGIA">IMPLANTOLOGIA</option>
                            <option value="PROSTODONCIA">PROSTODONCIA</option>
                            <option value="PERIODONCIA">PERIODONCIA</option>
                            <option value="PEDIATRIA">PEDIATRIA</option>
                          </select>
                        </FieldBlock>

                        <FieldBlock label="Recall (días)">
                          <input
                            type="number"
                            value={quickTreatmentForm.recallDays}
                            onChange={(e) =>
                              setQuickTreatmentForm((prev) => ({
                                ...prev,
                                recallDays: e.target.value,
                              }))
                            }
                            style={ui.input}
                            disabled={isReadOnly}
                          />
                        </FieldBlock>

                        <FieldBlock label="CondiciÃ³n odontograma">
                          <input
                            value={quickTreatmentForm.odontogramCondition}
                            onChange={(e) =>
                              setQuickTreatmentForm((prev) => ({
                                ...prev,
                                odontogramCondition: e.target.value.toUpperCase(),
                              }))
                            }
                            placeholder="Ej. CARIES"
                            style={ui.input}
                            disabled={isReadOnly || isDiagnosisPrefill}
                          />
                        </FieldBlock>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          style={ui.primaryButton}
                          onClick={handleQuickCreateTreatment}
                          disabled={savingQuickTreatment || isReadOnly}
                        >
                          {savingQuickTreatment ? "Guardando..." : "Crear y usar tratamiento"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                style={{
                  ...ui.primaryButton,
                  opacity: creatingPlan || isReadOnly ? 0.7 : 1,
                  cursor: creatingPlan || isReadOnly ? "not-allowed" : "pointer",
                }}
                disabled={creatingPlan || isReadOnly}
                onClick={createRealPlan}
              >
                {creatingPlan ? "Creando..." : "Crear plan real"}
              </button>
            </div>

            <div style={{ ...ui.metaText, fontSize: 12 }}>
              {isDiagnosisPrefill
                ? "AquÃ­ ya no se crea el plan a ciegas: primero debes resolver un tratamiento real del catÃ¡logo."
                : "AquÃ­ ya no se crea el plan a ciegas: primero debes resolver un tratamiento real del catÃ¡logo o del mapeo clÃ­nico."}
            </div>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard
        title="Planes"
        subtitle="Tratamientos, saldos y continuidad"
        actions={
          <button
            onClick={openManualPlanPage}
            style={{
              ...ui.secondaryButton,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
            }}
          >
            Nuevo plan manual
          </button>
        }
      >
        {!patientPlans?.length ? (
          <EmptyStateBlock
            title="Sin planes registrados"
            text="TodavÃ­a no hay un plan de tratamiento ligado a este paciente."
            compact
          />
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            {patientPlans.map((plan) => {
              const tone = getPlanTone(plan)
              const balance = getPlanBalance(plan)

              return (
                <div
                  key={plan.id}
                  style={{
                    border: `1px solid ${tone.border}`,
                    borderLeft: `4px solid ${tone.accent}`,
                    borderRadius: 18,
                    padding: 16,
                    background: tone.background,
                    boxShadow: "0 8px 22px rgba(15,23,42,0.05)",
                    display: "grid",
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 18,
                          color: "#0f172a",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {plan.title || "Plan de tratamiento"}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          marginTop: 8,
                        }}
                      >
                        <StatusPill tone="info">{translatePlanStatus(plan.status)}</StatusPill>
                        <StatusPill tone="neutral">
                          Estimado: {formatMoney(plan.totalEstimated || 0)}
                        </StatusPill>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        onClick={() => {
                          const params = new URLSearchParams()

                          if (clinicId) params.set("clinicId", String(clinicId))
                          if (selectedPatientId) params.set("patientId", String(selectedPatientId))
                          if (String(patientName || "").trim()) {
                            params.set("patientName", String(patientName).trim())
                          }

                          navigate(`/treatment-plans/${plan.id}?${params.toString()}`)
                        }}
                        style={{
                          ...ui.secondaryButton,
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          color: "#1d4ed8",
                        }}
                      >
                        Ver detalle
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: compact ? "1fr 1fr" : "repeat(4, minmax(0,1fr))",
                      gap: 10,
                    }}
                  >
                    <InlineMetric label="Estimado" value={formatMoney(plan.totalEstimated || 0)} />
                    <InlineMetric label="Pagado" value={formatMoney(plan.totalPaid || 0)} />
                    <InlineMetric label="Saldo" value={formatMoney(balance)} tone={balance > 0 ? "warning" : "success"} />
                    <InlineMetric label="Ãtems" value={String(plan.items?.length || 0)} />
                  </div>

                  {!plan.items?.length ? (
                    <EmptyStateBlock
                      title="Sin Ã­tems en este plan"
                      text="Este plan todavÃ­a no tiene tratamientos capturados."
                      compact
                    />
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {plan.items.map((item) => {
                        const alreadyScheduled = Boolean(item.appointmentId)
                        const isDone = item.status === "DONE"
                        const isCancelled = item.status === "CANCELLED"
                        const linkedDate = getLinkedAppointmentDate(item)

                        return (
                          <div
                            key={item.id}
                            style={{
                              border: "1px solid #e2e8f0",
                              background: "#ffffff",
                              borderRadius: 14,
                              padding: 12,
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            <div style={{ minWidth: 260, flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 900,
                                  color: "#0f172a",
                                  lineHeight: 1.25,
                                }}
                              >
                                {item.treatmentName || item.treatment?.name || "Tratamiento"}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginTop: 6,
                                }}
                              >
                                <StatusPill tone="info">{translatePlanItemStatus(item.status)}</StatusPill>

                                {item.tooth ? (
                                  <StatusPill tone="neutral">Pieza {item.tooth}</StatusPill>
                                ) : null}

                                {item.surface ? (
                                  <StatusPill tone="neutral">Superficie {item.surface}</StatusPill>
                                ) : null}

                                {item.assignedDoctorName ? (
                                  <StatusPill tone="neutral">
                                    Doctor {item.assignedDoctorName}
                                  </StatusPill>
                                ) : null}
                              </div>

                              <div style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>
                                Precio: {formatMoney(item.price || 0)}
                              </div>

                              {linkedDate ? (
                                <div style={{ fontSize: 12, color: "#0f766e", marginTop: 4 }}>
                                  Cita ligada: {new Date(linkedDate).toLocaleString("es-MX")}
                                </div>
                              ) : null}
                            </div>

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                onClick={() => schedulePlanItem(plan, item)}
                                disabled={alreadyScheduled || isDone || isCancelled || isReadOnly}
                                style={{
                                  ...ui.secondaryButton,
                                  background:
                                    alreadyScheduled || isDone || isCancelled || isReadOnly
                                      ? "#e2e8f0"
                                      : "#166534",
                                  border:
                                    alreadyScheduled || isDone || isCancelled || isReadOnly
                                      ? "1px solid #cbd5e1"
                                      : "1px solid #166534",
                                  color:
                                    alreadyScheduled || isDone || isCancelled || isReadOnly
                                      ? "#64748b"
                                      : "#ffffff",
                                  cursor:
                                    alreadyScheduled || isDone || isCancelled || isReadOnly
                                      ? "not-allowed"
                                      : "pointer",
                                }}
                              >
                                {alreadyScheduled
                                  ? "Ya agendado"
                                  : isDone
                                    ? "Realizado"
                                    : isCancelled
                                      ? "Cancelado"
                                      : isReadOnly
                                        ? "Solo lectura"
                                        : "Agendar"}
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>
    </div>
  )
}


function translatePlanStatus(status) {
  const raw = String(status || "").trim().toUpperCase()

  if (raw === "ACTIVE") return "Activo"
  if (raw === "DRAFT") return "Borrador"
  if (raw === "COMPLETED") return "Completado"
  if (raw === "CANCELLED") return "Cancelado"
  if (raw === "PAUSED") return "Pausado"
  if (!raw) return "Sin estado"

  return raw
}

function translatePlanItemStatus(status) {
  const raw = String(status || "").trim().toUpperCase()

  if (raw === "PENDING") return "Pendiente"
  if (raw === "SCHEDULED") return "Agendado"
  if (raw === "IN_PROGRESS") return "En proceso"
  if (raw === "DONE") return "Realizado"
  if (raw === "COMPLETED") return "Completado"
  if (raw === "CANCELLED") return "Cancelado"
  if (!raw) return "Sin estado"

  return raw
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <div
        style={{
          ...ui.label,
          marginBottom: 6,
          fontWeight: 800,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}

function InlineMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#ffffff",
      border: "#e2e8f0",
      value: "#0f172a",
    },
    success: {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      value: "#166534",
    },
    warning: {
      bg: "#fffbeb",
      border: "#fde68a",
      value: "#92400e",
    },
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 14,
        padding: 12,
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 5,
          fontSize: 15,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
    </div>
  )
}
